import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { canAccessSystemSettings } from '@/api/adapters'
import { checkMobile } from '@/utils/device'
import { loadPageData } from '@/services/dataService'

Vue.use(VueRouter)

function homePath() {
  return store.getters['auth/isAuthenticated'] ? '/dashboard' : '/login'
}

function isNavigationFailure(err) {
  return (
    err &&
    (err.name === 'NavigationDuplicated' ||
      err.name === 'NavigationCancelled' ||
      /Navigation cancelled/i.test(err.message || ''))
  )
}

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => {
    if (isNavigationFailure(err)) return err
    return Promise.reject(err)
  })
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject)
  return originalReplace.call(this, location).catch(err => {
    if (isNavigationFailure(err)) return err
    return Promise.reject(err)
  })
}

const MainLayout = () => import('@/layouts/MainLayout.vue')
const MobileLayout = () => import('@/layouts/MobileLayout.vue')

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { public: true, title: '登录' }
  },
  {
    path: '/',
    component: () => (checkMobile() ? MobileLayout() : MainLayout()),
    redirect: homePath,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '实时看板', mobile: true, mobileGroup: 'dashboard' }
      },
      {
        path: 'm/work',
        name: 'MobileWorkHub',
        component: () => import('@/views/MobileModuleHub.vue'),
        meta: { title: '作业中心', mobile: true, mobileGroup: 'work', hubKey: 'work' }
      },
      {
        path: 'm/setup',
        name: 'MobileSetupHub',
        component: () => import('@/views/MobileModuleHub.vue'),
        meta: { title: '基础配置', mobile: true, mobileGroup: 'setup', hubKey: 'setup' }
      },
      {
        path: 'zone',
        name: 'ZoneManage',
        component: () => import('@/views/ZoneManage.vue'),
        meta: { title: '工区管理', mobile: true, mobileGroup: 'setup' }
      },
      {
        path: 'device',
        name: 'DeviceManage',
        component: () => import('@/views/DeviceManage.vue'),
        meta: { title: '设备管理', mobile: true, mobileGroup: 'setup' }
      },
      {
        path: 'alarm',
        name: 'AlarmManage',
        component: () => import('@/views/AlarmManage.vue'),
        meta: { title: '报警记录', mobile: true, mobileGroup: 'work' }
      },
      {
        path: 'job-type',
        name: 'JobTypeManage',
        component: () => import('@/views/JobTypeManage.vue'),
        meta: { title: '工种管理', mobile: true, mobileGroup: 'setup' }
      },
      {
        path: 'trace',
        name: 'TracePlayback',
        component: () => import('@/views/TracePlayback.vue'),
        meta: { title: '轨迹回放', mobile: true, mobileGroup: 'work' }
      },
      {
        path: 'work-stats',
        name: 'WorkStats',
        component: () => import('@/views/WorkStats.vue'),
        meta: { title: '作业统计', mobile: true, mobileGroup: 'work' }
      },
      {
        path: 'settings',
        name: 'SystemSettings',
        component: () => import('@/views/SystemSettings.vue'),
        meta: { title: '系统设置', mobile: false }
      }
    ]
  },
  {
    path: '*',
    redirect: homePath
  }
]

const router = new VueRouter({
  mode: 'hash',
  routes
})

router.beforeEach((to, from, next) => {
  const isLoggedIn = store.getters['auth/isAuthenticated']
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  if (to.meta.public) {
    if (isLoggedIn && to.name === 'Login') {
      next({ name: 'Dashboard' })
    } else {
      next()
    }
    return
  }

  if (requiresAuth && !isLoggedIn) {
    if (to.name === 'Login') {
      next()
      return
    }
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }

  if (checkMobile() && to.meta.mobile === false) {
    next({ name: 'Dashboard' })
    return
  }

  if (to.name === 'SystemSettings' && !canAccessSystemSettings(store.state.auth.user)) {
    next({ name: 'Dashboard' })
    return
  }

  next()
})

router.afterEach(to => {
  if (to.meta.public) return
  if (!store.getters['auth/isAuthenticated']) return
  if (!to.name) return
  loadPageData(to.name, store.state.auth.user).catch(() => {})
})

export default router
